import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { env } from '@shared/config/env';

/**
 * HTTP-клиент для работы с richat-next API.
 *
 * Auth-стратегия (от бэка):
 * - токены хранятся в httpOnly cookies (access_token, refresh_token)
 * - бэк сам читает их из заголовка Cookie на каждом запросе
 * - withCredentials: true чтобы браузер слал куки на cross-origin
 * - на 401 (access протух) — пытаемся /auth/refresh, потом повторяем запрос
 */

let isRefreshing = false;
let refreshWaiters: Array<(ok: boolean) => void> = [];

function notifyWaiters(ok: boolean) {
  refreshWaiters.forEach((cb) => cb(ok));
  refreshWaiters = [];
}

export const httpClient: AxiosInstance = axios.create({
  baseURL: env.API_BASE_URL,
  withCredentials: true,
  timeout: 30_000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Перехватчик запросов — на будущее (логирование, X-Request-ID и т.д.)
httpClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    if (env.IS_DEV) {
      // eslint-disable-next-line no-console
      console.debug('[http]', config.method?.toUpperCase(), config.url);
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Перехватчик ответов — обработка 401 с авто-рефрешем
httpClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as InternalAxiosRequestConfig & { _retried?: boolean };

    if (!original || error.response?.status !== 401 || original._retried) {
      return Promise.reject(error);
    }

    // Не пытаемся рефрешить сам auth-эндпоинт.
    if (original.url?.startsWith('/auth/')) {
      return Promise.reject(error);
    }

    original._retried = true;

    if (isRefreshing) {
      // Ждём пока другой запрос рефрешит
      return new Promise((resolve, reject) => {
        refreshWaiters.push((ok) => {
          if (ok) {
            httpClient(original).then(resolve).catch(reject);
          } else {
            reject(error);
          }
        });
      });
    }

    isRefreshing = true;
    try {
      await httpClient.post('/auth/refresh');
      notifyWaiters(true);
      return httpClient(original);
    } catch (refreshError) {
      notifyWaiters(false);
      // Рефреш не сработал — выкидываем юзера на /login.
      // window.location, потому что мы вне React-дерева.
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
        // Запоминаем страницу — после входа вернём на неё (см. app/router/returnTo.ts).
        try {
          window.sessionStorage.setItem('richat.returnTo', window.location.pathname + window.location.search);
        } catch {
          /* ignore */
        }
        window.location.assign('/login');
      }
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);
